import { getAuthFlowTypeForSignIn } from '@/utils/aws/cognito/config';
import { fetchAuthSession } from '@aws-amplify/core';
import {
  confirmResetPassword,
  confirmSignIn,
  getCurrentUser,
  resetPassword,
  signIn,
  updatePassword,
} from 'aws-amplify/auth';

export type LegacyAuthChallengeResponse = {
  challengeName: string | undefined;
  challengeParam: Record<string, unknown>;
};

export type LegacyAuthenticatedUser = {
  username: string;
  signInUserSession: {
    idToken: { jwtToken: string | undefined; payload: Record<string, unknown> };
    accessToken: {
      jwtToken: string | undefined;
      payload: Record<string, unknown>;
    };
  };
};

export type LegacyAuthSignInResult =
  | LegacyAuthenticatedUser
  | LegacyAuthChallengeResponse;

type AuthErrorLike = {
  code?: string;
  name?: string;
  message?: string;
};

/** Maps v6 AuthError.name to legacy Cognito .code for existing call sites. */
export function normalizeAuthError(err: unknown): never {
  if (err && typeof err === 'object') {
    const authErr = err as AuthErrorLike;
    if (!authErr.code && authErr.name) {
      authErr.code = authErr.name;
    }
  }
  throw err;
}

async function withNormalizedAuthError<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    normalizeAuthError(err);
  }
}

export async function getLegacyCurrentAuthenticatedUserFromClient(): Promise<LegacyAuthenticatedUser> {
  const user = await getCurrentUser();
  const session = await fetchAuthSession();

  return {
    username: user.username,
    signInUserSession: {
      idToken: {
        jwtToken: session.tokens?.idToken?.toString(),
        payload: session.tokens?.idToken?.payload ?? {},
      },
      accessToken: {
        jwtToken: session.tokens?.accessToken?.toString(),
        payload: session.tokens?.accessToken?.payload ?? {},
      },
    },
  };
}

export function mapSignInStepToLegacyChallengeName(
  signInStep: string | undefined
): string | undefined {
  if (signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
    return 'CUSTOM_CHALLENGE';
  }
  if (signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
    return 'NEW_PASSWORD_REQUIRED';
  }
  return signInStep;
}

export function getLegacyChallengeParam(
  nextStep: unknown
): Record<string, unknown> {
  if (!nextStep || typeof nextStep !== 'object') {
    return {};
  }

  if (!('additionalInfo' in nextStep)) {
    return {};
  }

  const { additionalInfo } = nextStep as {
    additionalInfo?: Record<string, unknown>;
  };
  if (!additionalInfo) {
    return {};
  }
  return additionalInfo;
}

async function mapSignInResultToLegacy(
  result: Awaited<ReturnType<typeof signIn>>
): Promise<LegacyAuthSignInResult> {
  if (result.isSignedIn) {
    return getLegacyCurrentAuthenticatedUserFromClient();
  }

  return {
    challengeName: mapSignInStepToLegacyChallengeName(
      result.nextStep?.signInStep
    ),
    challengeParam: getLegacyChallengeParam(result.nextStep),
  };
}

async function mapConfirmSignInResultToLegacy(
  result: Awaited<ReturnType<typeof confirmSignIn>>
): Promise<LegacyAuthSignInResult> {
  if (result.isSignedIn) {
    return getLegacyCurrentAuthenticatedUserFromClient();
  }

  return {
    challengeName: mapSignInStepToLegacyChallengeName(
      result.nextStep?.signInStep
    ),
    challengeParam: getLegacyChallengeParam(result.nextStep),
  };
}

export async function signInWithPassword(
  username: string,
  password: string
): Promise<LegacyAuthSignInResult> {
  return withNormalizedAuthError(async () => {
    const result = await signIn({
      username,
      password,
      options: { authFlowType: getAuthFlowTypeForSignIn() },
    });
    return mapSignInResultToLegacy(result);
  });
}

export async function confirmCustomChallengeAnswer(
  _cognitoUser: unknown,
  challengeResponse: string,
  clientMetadata?: Record<string, string>
): Promise<LegacyAuthSignInResult> {
  return withNormalizedAuthError(async () => {
    const result = await confirmSignIn({
      challengeResponse,
      options: {
        clientMetadata,
      },
    });
    return mapConfirmSignInResultToLegacy(result);
  });
}

export async function completeNewPasswordChallenge(
  _cognitoUser: unknown,
  newPassword: string
): Promise<LegacyAuthSignInResult> {
  return withNormalizedAuthError(async () => {
    const result = await confirmSignIn({
      challengeResponse: newPassword,
    });
    return mapConfirmSignInResultToLegacy(result);
  });
}

export async function requestPasswordReset(
  username: string
): Promise<Awaited<ReturnType<typeof resetPassword>>> {
  return withNormalizedAuthError(() => resetPassword({ username }));
}

export async function confirmPasswordReset(
  username: string,
  confirmationCode: string,
  newPassword: string
): Promise<Awaited<ReturnType<typeof confirmResetPassword>>> {
  return withNormalizedAuthError(() =>
    confirmResetPassword({
      username,
      confirmationCode,
      newPassword,
    })
  );
}

export async function changeUserPassword(
  oldPassword: string,
  newPassword: string
): Promise<Awaited<ReturnType<typeof updatePassword>>> {
  return withNormalizedAuthError(() =>
    updatePassword({ oldPassword, newPassword })
  );
}
